// Guillotine (BSP) photo-layout engine, shared by every multi-photo
// builder section (collage, and — as they're migrated — canvas, mug,
// greeting card). See docs/plans/collage-layout.md for the design and
// docs/plans/shared-layout-engine.md for the migration this file is part
// of. scripts/test-layout-engine.js is the measurement harness — re-run it
// after any change here.
//
// Pure geometry only: every function here takes and returns plain
// {w, h}-style frames and photo descriptors. Nothing in this file touches
// the DOM, fetch, or any product-specific concept (units, wrap/fold
// allowances, page/panel structure) - that lives in each consuming
// section's own wiring layer.
//
// Loaded as a plain global (window.GuillotineLayout) in the browser - see
// each consuming section for the <script src> tag, placed before that
// section's own inline <script> so load order is a basic HTML parsing
// guarantee, not a defer/async inference (same pattern already used here
// for the SortableJS <script> tag). Also require()-able directly in Node
// for the test harness, via the module.exports branch below.
(function (root) {
  'use strict';

  const LAYOUT_MIN_RATIO = 0.3;
  const LAYOUT_MAX_RATIO = 0.7;
  const LAYOUT_ASPECT_MIN = 0.5;
  const LAYOUT_ASPECT_MAX = 2.0;
  const LAYOUT_GAP_MM = 2.5;
  const LAYOUT_MARGIN_MM = 5;

  function makeLeaf(uuid) { return { type: 'leaf', uuid }; }
  function makeSplit(direction, ratio, first, second) { return { type: 'split', direction, ratio, first, second }; }
  function clampNum(v, lo, hi) { return Math.min(Math.max(v, lo), hi); }

  // A "frame" is a leaf/subtree's resolved size in mm plus which of its 4
  // sides are still the canvas edge (true) vs an internal cut shared with
  // a sibling (false). Needed so inset can use MARGIN_MM on canvas edges
  // and GAP_MM/2 on internal edges, per leaf, without baking gap into the
  // split geometry itself.
  // fullBleed: true means "no inset at all, on any side" - used for the
  // single-photo case (see buildTree/leafList below), which has nothing to
  // separate from anything else, rather than "boundary sides get
  // MARGIN_MM" like every other frame.
  function rootFrame(dims, fullBleed) { return { w: dims.w, h: dims.h, top: true, right: true, bottom: true, left: true, fullBleed: !!fullBleed }; }

  function divideFrame(frame, direction, ratio) {
    if (direction === 'horizontal') { // horizontal cut line -> top/bottom children
      const hFirst = frame.h * ratio;
      return [
        { w: frame.w, h: hFirst,           top: frame.top, right: frame.right,  bottom: false,        left: frame.left },
        { w: frame.w, h: frame.h - hFirst, top: false,      right: frame.right,  bottom: frame.bottom, left: frame.left }
      ];
    }
    // vertical cut line -> left/right children
    const wFirst = frame.w * ratio;
    return [
      { w: wFirst,           h: frame.h, top: frame.top, right: false,       bottom: frame.bottom, left: frame.left },
      { w: frame.w - wFirst, h: frame.h, top: frame.top, right: frame.right, bottom: frame.bottom, left: false }
    ];
  }

  function visibleDims(frame) {
    if (frame.fullBleed) return { w: frame.w, h: frame.h };
    const insetX = (frame.left ? LAYOUT_MARGIN_MM : LAYOUT_GAP_MM / 2) + (frame.right ? LAYOUT_MARGIN_MM : LAYOUT_GAP_MM / 2);
    const insetY = (frame.top ? LAYOUT_MARGIN_MM : LAYOUT_GAP_MM / 2) + (frame.bottom ? LAYOUT_MARGIN_MM : LAYOUT_GAP_MM / 2);
    return { w: Math.max(frame.w - insetX, 0.1), h: Math.max(frame.h - insetY, 0.1) };
  }

  function visibleAspect(frame) { const v = visibleDims(frame); return v.w / v.h; }

  // maxAspect optional, defaults to LAYOUT_ASPECT_MAX - every existing
  // call site omits it, so this is byte-identical for them. Threaded
  // through so ensureAspect (below) can be told about the same relaxed
  // ceiling chooseSplit's orientation-preference path already used,
  // instead of independently re-deriving a stricter answer and silently
  // overriding that choice - see buildSubtree's comment for why this
  // matters. LAYOUT_ASPECT_MIN is exactly 1/LAYOUT_ASPECT_MAX (0.5 ==
  // 1/2.0), so deriving min from whatever max is passed keeps the same
  // symmetric band shape at any tolerance.
  function aspectError(frame, maxAspect) {
    const max = maxAspect === undefined ? LAYOUT_ASPECT_MAX : maxAspect;
    const min = 1 / max;
    const a = visibleAspect(frame);
    if (a < min) return min - a;
    if (a > max) return a - max;
    return 0;
  }

  // Orientation vote: used only as a tie-break preference inside
  // chooseSplit below, not as the primary decision - see that function's
  // comment for why (measurement showed orientation-only choice leaves a
  // third of realistic layouts with a letterboxed cell; it has no
  // foresight of what's achievable several splits down).
  function voteDirection(photos, frame) {
    let landscape = 0, portrait = 0;
    photos.forEach(p => {
      const r = p.width / p.height;
      if (r > 1.05) landscape++; else if (r < 0.95) portrait++;
    });
    if (landscape > portrait) return 'horizontal'; // stack top/bottom
    if (portrait > landscape) return 'vertical';   // side by side
    return (frame.w >= frame.h) ? 'vertical' : 'horizontal'; // tie: cut the longer axis
  }

  // Whether every photo shares the same broad orientation (all landscape
  // or all portrait) - shared by chooseSplit's orientation-preference
  // path and buildSubtree's matching ensureAspect relaxation, so the two
  // can never disagree about when the exception applies.
  function isHomogeneousOrientation(photos) {
    const landscapeCount = photos.filter(p => p.width / p.height > 1.05).length;
    const portraitCount = photos.filter(p => p.width / p.height < 0.95).length;
    return landscapeCount === photos.length || portraitCount === photos.length;
  }

  // Adjusts { direction, ratio } for one split so whichever side(s) are a
  // single photo (singleSides: 'A' | 'B' | 'both') land in the accepted
  // post-inset visible-aspect band. Tries nudging ratio within the clamp
  // range first, then flipping direction for this split only (a local
  // override, doesn't change direction choice elsewhere in the tree). If
  // neither fully fixes it, returns whichever option minimized the error
  // rather than failing. This is the FINE, real-mm/inset-aware pass, run
  // only at splits adjacent to a leaf - see chooseSplit for the coarse,
  // scale-invariant pass that now also runs at every OTHER split, which is
  // what actually fixes the "greedy, no foresight" problem.
  //
  // relaxedMax optional (defaults through to aspectError's own default,
  // LAYOUT_ASPECT_MAX): without it this function is unchanged from
  // before - both existing call sites (buildSubtree's default case,
  // insertLeaf) omit it. buildSubtree passes it through for a homogeneous
  // group under a seed, so this fine pass judges the split by the SAME
  // tolerance chooseSplit's orientation-preference already used to pick
  // it - otherwise this always-strict pass would silently correct a
  // deliberately-relaxed choice straight back to the old shape, the
  // orientation preference having no visible effect at all (measured:
  // exactly this happened before this parameter existed).
  function ensureAspect(frame, direction, ratio, singleSides, relaxedMax) {
    function errFor(dir, r) {
      const [a, b] = divideFrame(frame, dir, r);
      const errA = singleSides === 'B' ? 0 : aspectError(a, relaxedMax);
      const errB = singleSides === 'A' ? 0 : aspectError(b, relaxedMax);
      return errA + errB;
    }

    const step = 0.02;
    let bestRatio = ratio, bestErr = errFor(direction, ratio);
    if (bestErr === 0) return { direction, ratio };

    for (let r = LAYOUT_MIN_RATIO; r <= LAYOUT_MAX_RATIO; r += step) {
      const err = errFor(direction, r);
      if (err < bestErr) { bestErr = err; bestRatio = r; }
    }
    if (bestErr === 0) return { direction, ratio: bestRatio };

    const flipped = direction === 'horizontal' ? 'vertical' : 'horizontal';
    let bestFlipRatio = ratio, bestFlipErr = errFor(flipped, ratio);
    for (let r = LAYOUT_MIN_RATIO; r <= LAYOUT_MAX_RATIO; r += step) {
      const err = errFor(flipped, r);
      if (err < bestFlipErr) { bestFlipErr = err; bestFlipRatio = r; }
    }

    return (bestFlipErr < bestErr) ? { direction: flipped, ratio: bestFlipRatio } : { direction, ratio: bestRatio };
  }

  // --- Bounded lookahead ---------------------------------------------------
  // Fixes the root cause measured in the test script: a purely greedy,
  // top-down build chooses each split with zero visibility into whether
  // that choice paints a descendant leaf into an unfixable corner several
  // levels down - ensureAspect can only patch the split it's called on, not
  // undo an ancestor's decision. estimateWorstAspect answers "what's the
  // best achievable worst-case leaf aspect for N photos in a frame of this
  // (pre-inset, scale-invariant) shape" without materializing any real
  // subtree, so chooseSplit can pick direction/ratio at EVERY split - not
  // just leaf-adjacent ones - with real foresight, cheaply. Memoized on
  // (count, aspect-bucket) only, since without inset the problem is scale-
  // invariant; this collapses what would otherwise be exponential search
  // (candidates^depth) into a small, fast, shared-subproblem cache.
  const LAYOUT_RATIO_SAMPLES = [0.30, 0.35, 0.40, 0.45, 0.50, 0.55, 0.60, 0.65, 0.70];
  const LAYOUT_DIRECTIONS = ['horizontal', 'vertical'];
  const estimateCache = new Map();

  function estimateWorstAspect(count, aspectWH) {
    if (count <= 1) return Math.max(aspectWH, 1 / aspectWH);
    const key = count + '|' + Math.round(aspectWH * 200);
    if (estimateCache.has(key)) return estimateCache.get(key);

    const countA = Math.round(count / 2), countB = count - countA;
    let best = Infinity;
    LAYOUT_DIRECTIONS.forEach(dir => {
      LAYOUT_RATIO_SAMPLES.forEach(r => {
        const aspectA = dir === 'vertical' ? aspectWH * r       : aspectWH / r;
        const aspectB = dir === 'vertical' ? aspectWH * (1 - r) : aspectWH / (1 - r);
        const score = Math.max(estimateWorstAspect(countA, aspectA), estimateWorstAspect(countB, aspectB));
        if (score < best) best = score;
      });
    });
    estimateCache.set(key, best);
    return best;
  }

  // Picks { direction, ratio } for a real split using the estimate above,
  // scored on the REAL groupA/groupB counts and the real frame's (pre-inset)
  // aspect. Orientation vote only breaks near-ties (within TIE_EPS) between
  // otherwise equally-good candidates - aspect correctness takes priority,
  // orientation-matching is the secondary preference it was always meant
  // to be.
  //
  // variationSeed (optional, 2026-09-12): when omitted, this function is
  // completely unchanged from before - every existing consumer (collage,
  // and canvas/mug/greeting-card as they migrate) calls buildTree without
  // it and gets the exact same single best-scoring candidate as always.
  // When provided (photobook only, so far - "shuffle only shuffles
  // photos, not layout" was the actual ask, and reordering photos alone
  // barely changes this function's decision, since it scores by group
  // COUNT not which specific photos are in a group), collects every
  // candidate within LAYOUT_VARIATION_TOLERANCE of the true best score -
  // the aspect-band guarantee is never compromised, only *which*
  // similarly-good geometry gets used - and picks deterministically among
  // them from the seed, so a genuinely different split (not just a
  // different photo-to-slot assignment) is possible, while the exact same
  // seed always reproduces the exact same tree (stable across re-renders,
  // e.g. draft restore).
  const LAYOUT_TIE_EPS = 0.02;
  // Measured, not guessed: swept 0.12/0.20/0.30/0.40 across n=3,5,6,8,9 with
  // 20 seeds each. 0.30 is the highest tried that still held 100%
  // aspect-band compliance (worst seen 1.99, just under the 2.0 ceiling) -
  // 0.40 already breached it (99.3%, worst 2.05). At 0.30, distinct shapes
  // per 20 seeds: n=3:10, n=5:18, n=6:18, n=8:20, n=9:20 - real variety
  // without ever trading away the aspect-band guarantee.
  const LAYOUT_VARIATION_TOLERANCE = 0.30;
  // Bounded exception to LAYOUT_ASPECT_MAX (2.0), only reachable via the
  // homogeneous-orientation path below: a full-width/full-height single-
  // photo band for an all-landscape or all-portrait group is allowed up
  // to this aspect instead of 2.0. Sized directly against measured,
  // MARGIN/GAP-inset-adjusted numbers (not raw frame w/h) for 3
  // same-orientation photos on a 4x6 page's sampled ratios: r=0.35 is
  // the only one where BOTH the full-width single band (visible aspect
  // 2.382) and the 2-photo band it leaves behind (score 2.308, its own
  // best achievable split) clear the same ceiling - r=0.30 and r=0.40
  // each fail one side or the other even at this ceiling, which is why
  // this needs the score check alongside it (see where this is used).
  const LAYOUT_ORIENTATION_ASPECT_MAX = 2.4;
  // A separate, larger allowance for the 'both' case only (exactly 2
  // same-orientation photos, both sides single, nothing left to
  // recurse into) - "two full rows" for 2 landscape photos needs the
  // resulting band at visible aspect ~3.20 on a 4x6 page (measured:
  // symmetric 50/50 split, margin-inset included), well past what's
  // reasonable to allow generally (LAYOUT_ORIENTATION_ASPECT_MAX above
  // stays conservative because a recursing multi-photo side's own
  // fit-quality, not just compliance, matters there - buildSubtreeInternal's
  // ok-propagation only guarantees no violation, not that a wide
  // allowance still looks good several levels deep). The 'both' case
  // has no such concern: it's exactly 2 leaves, fully resolved right
  // here, so a wider allowance is a direct, understood trade for the
  // specific shape asked for, not a hidden risk further down the tree.
  const LAYOUT_ORIENTATION_BOTH_ASPECT_MAX = 3.3;
  // allowOrientation (default true) lets buildSubtree ask for the plain,
  // pre-existing behavior on retry - see buildSubtree's verify-and-
  // fallback comment for why a second, unpreferenced attempt is needed
  // at all: estimateWorstAspect's score is scale-invariant and doesn't
  // know about the fixed-mm margin/gap inset, so it can promise a
  // homogeneous group's multi-photo side will turn out fine when, at a
  // small enough absolute frame size several levels deep, it actually
  // can't - only a real rebuild of that side can catch this, not another
  // estimate.
  function chooseSplit(frame, groupA, groupB, variationSeed, allowOrientation) {
    if (allowOrientation === undefined) allowOrientation = true;
    const aspectWH = frame.w / frame.h;
    const preferred = voteDirection(groupA.concat(groupB), frame);

    if (variationSeed === undefined) {
      let best = null, bestScore = Infinity;
      LAYOUT_DIRECTIONS.forEach(dir => {
        LAYOUT_RATIO_SAMPLES.forEach(r => {
          const aspectA = dir === 'vertical' ? aspectWH * r       : aspectWH / r;
          const aspectB = dir === 'vertical' ? aspectWH * (1 - r) : aspectWH / (1 - r);
          const score = Math.max(estimateWorstAspect(groupA.length, aspectA), estimateWorstAspect(groupB.length, aspectB));
          const better = score < bestScore - LAYOUT_TIE_EPS
            || (Math.abs(score - bestScore) <= LAYOUT_TIE_EPS && dir === preferred && best && best.direction !== preferred);
          if (better || best === null) { bestScore = Math.min(score, bestScore); best = { direction: dir, ratio: r }; }
        });
      });
      return best;
    }

    const candidates = [];
    LAYOUT_DIRECTIONS.forEach(dir => {
      LAYOUT_RATIO_SAMPLES.forEach(r => {
        const aspectA = dir === 'vertical' ? aspectWH * r       : aspectWH / r;
        const aspectB = dir === 'vertical' ? aspectWH * (1 - r) : aspectWH / (1 - r);
        const score = Math.max(estimateWorstAspect(groupA.length, aspectA), estimateWorstAspect(groupB.length, aspectB));
        candidates.push({ direction: dir, ratio: r, score });
      });
    });
    candidates.sort((a, b) => a.score - b.score);
    const bestScore = candidates[0].score;
    let nearBest = candidates.filter(c => c.score <= bestScore + LAYOUT_VARIATION_TOLERANCE);

    // Orientation-aware direction preference (photobook only - this
    // whole branch only runs when a seed is passed): "3 landscape
    // photos... could be two across the top and a full-width strip
    // across the bottom" - a group that's ENTIRELY one orientation reads
    // best when the split direction gives that orientation a full-width
    // (landscape -> horizontal, stacked rows) or full-height (portrait
    // -> vertical, side-by-side columns) band, not whichever direction
    // happens to win on abstract aspect-band score alone. Restricted to
    // a fully homogeneous group (every photo the same broad orientation)
    // - a mixed group's vote is one photo's aspect away from flipping
    // either way, so no preference is asserted for it.
    const homogeneous = allowOrientation && isHomogeneousOrientation(groupA.concat(groupB));

    // A split with a single-photo side gets silently corrected by
    // ensureAspect (called right after this returns, in buildSubtree)
    // whenever that side lands out-of-band - deterministically, ignoring
    // the seed - which would quietly cancel out the variation this whole
    // path exists for. Measured live: every n=3 split hits this (mid=2,
    // rest=1 is always a single-photo side), and real pages with
    // different photos but the same n=3 shape all converged on the exact
    // same geometry despite genuinely different seeds, until this filter
    // was added. Keep only near-best candidates that already land in-band
    // on their own, so the seed's choice actually survives; fall back to
    // the plain best candidate (today's exact choice) if none qualify -
    // correctness first, variation second, never the other way round.
    const singleA = groupA.length === 1, singleB = groupB.length === 1;
    if (singleA || singleB) {
      const strictSurvivors = nearBest.filter(c => {
        const [fa, fb] = divideFrame(frame, c.direction, clampNum(c.ratio, LAYOUT_MIN_RATIO, LAYOUT_MAX_RATIO));
        return (!singleA || aspectError(fa) === 0) && (!singleB || aspectError(fb) === 0);
      });
      let chosen = strictSurvivors.length > 0 ? strictSurvivors : [candidates[0]];

      // Measured: "2 across the top, full-width across the bottom" for 3
      // same-orientation photos on a 4x6 page needs the full-width
      // single-photo band at aspect ~2.14 - just past the standard 2.0
      // ceiling, which is why this shape was never reachable before.
      // LAYOUT_ORIENTATION_ASPECT_MAX gives a small, explicit, bounded
      // allowance ONLY for this case (a homogeneous group, a single-photo
      // side, direction already matching that group's own orientation) -
      // confirmed acceptable by direct product decision (2026-09-12), not
      // assumed. Every other caller's photos, and every non-single-side
      // split even for photobook, still get the strict 2.0 ceiling
      // untouched - collage/canvas/mug/greeting-card never pass a seed,
      // so none of this new allowance is reachable from them at all.
      if (homogeneous) {
        const strictOriented = chosen.filter(c => c.direction === preferred);
        if (strictOriented.length > 0) {
          chosen = strictOriented;
        } else {
          // 'both' (exactly 2 photos, nothing left to recurse into) gets
          // the larger BOTH_ASPECT_MAX allowance - see that constant's
          // comment for why a wider allowance is safe there but not in
          // general.
          const ceiling = (singleA && singleB) ? LAYOUT_ORIENTATION_BOTH_ASPECT_MAX : LAYOUT_ORIENTATION_ASPECT_MAX;
          const relaxedOriented = candidates.filter(c => {
            if (c.direction !== preferred) return false;
            // The literal check covers the single-photo side directly;
            // c.score (estimateWorstAspect, covering BOTH sides) has to
            // be checked too, or a candidate that just barely passes the
            // single side can still starve the OTHER (multi-photo) side
            // into something far worse a level down - measured: at
            // ratio=0.30 a 3-landscape page's single bottom band passes
            // fine (2.14), but the 2-photo top band it leaves behind
            // then can't do better than ~2.5 for itself, no matter how
            // it's split. Requiring the whole candidate's score under
            // the same ceiling rules that candidate out in favor of one
            // that actually leaves the other side enough room.
            if (c.score > ceiling) return false;
            const [fa, fb] = divideFrame(frame, c.direction, clampNum(c.ratio, LAYOUT_MIN_RATIO, LAYOUT_MAX_RATIO));
            const aA = visibleAspect(fa), aB = visibleAspect(fb);
            const okA = !singleA || (aA <= ceiling && aA >= 1 / ceiling);
            const okB = !singleB || (aB <= ceiling && aB >= 1 / ceiling);
            return okA && okB;
          });
          if (relaxedOriented.length > 0) chosen = relaxedOriented;
        }
      }

      nearBest = chosen;
    } else if (homogeneous) {
      // Neither side is a single photo here, so nothing about this
      // split is directly leaf-verifiable the way singleA/singleB is
      // above - filtering the WHOLE near-best pool (candidates within
      // LAYOUT_VARIATION_TOLERANCE of best) down to direction-matches
      // trusts estimateWorstAspect's word that any of them will
      // recurse out fine. That trust used to be misplaced (measured:
      // n=9 portrait, 3000 seeds - 11/27000 leaves over 2.4 with no
      // preference at all, 817/27000 with this filter alone) - but
      // buildSubtreeInternal's ok-propagation (see below) now actually
      // rebuilds and verifies the result instead of trusting the
      // estimate, and falls back cleanly when it doesn't hold up. Same
      // wide sweep with that in place: 0/27000 over 2.4. So the full
      // near-best pool is safe here after all - the correctness
      // guarantee comes from actually checking the outcome, not from
      // shrinking the candidate pool this filter picks from, which
      // would only have cost Shuffle variety for no remaining reason.
      const orientationMatches = nearBest.filter(c => c.direction === preferred);
      if (orientationMatches.length > 0) nearBest = orientationMatches;
    }

    // Balance preference (seeded only): "nine portraits pushing two
    // images too small" - measured, real cause: ratio only ever
    // optimizes aspect-band compliance, with nothing preferring an area
    // split that matches each side's PHOTO COUNT. A count-blind ratio
    // can satisfy the aspect band while still giving a 2-photo side
    // barely any area if that's what scored best abstractly - measured
    // n=9 portrait, 500 seeds: worst-case smallest/largest leaf area
    // ratio 0.051 (one photo at ~5% the area of the biggest), average
    // 0.244. `ratio` IS the area fraction groupA gets regardless of
    // direction (the other axis is shared by both sides), so comparing
    // it directly to groupA's share of the photo count is a correct,
    // cheap balance signal.
    //
    // A fixed window around the ideal (tried first) made things WORSE
    // on average across n=3-12 (0.502 -> 0.409 mean min/max-area ratio)
    // by admitting extra candidates into cases that were already
    // perfectly balanced with no help needed (n=4, n=8: naturally 1.000
    // both orientations, diluted to ~0.7 once other near-best ratios
    // became seed-selectable) - variety and balance were fighting each
    // other. Tie-breaking against the BEST balance actually achievable
    // in this specific near-best pool (never a fixed number) avoids
    // that: it can only pick among whatever ties for closest-to-ideal,
    // so an already-perfect case stays perfect, and an imperfect one
    // (n=9 portrait) still gets pulled toward its own best available
    // option (0.244 -> 0.469 average, same measurement).
    if (nearBest.length > 1) {
      const idealRatio = groupA.length / (groupA.length + groupB.length);
      const bestBalanceDiff = Math.min.apply(null, nearBest.map(c => Math.abs(c.ratio - idealRatio)));
      nearBest = nearBest.filter(c => Math.abs(c.ratio - idealRatio) <= bestBalanceDiff + LAYOUT_TIE_EPS);
    }

    const idx = Math.abs(variationSeed) % nearBest.length;
    return { direction: nearBest[idx].direction, ratio: nearBest[idx].ratio };
  }

  // photos: [{ uuid, width, height }]. dims: { w, h } real physical mm -
  // the usable area to fill (see findUsableFrame). A single photo is
  // always full-bleed: nothing to separate it from, so no margin, no gap,
  // no aspect-band check ever runs for it (see rootFrame's fullBleed flag
  // and buildSubtree's n===1 base case, which returns before any of that
  // logic is reachable).
  // variationSeed: optional, see chooseSplit's comment. Omitted by every
  // existing caller (collage etc.) - identical behavior to before this was
  // added. When provided, mixed differently for each recursive branch
  // (mixSeed below) so a whole tree doesn't make the same "how far from
  // best" choice at every split - each level gets its own independent draw
  // from the same deterministic seed.
  function mixSeed(seed, salt) {
    return (Math.imul(seed ^ salt, 2654435761) ^ (seed >>> 13)) | 0;
  }

  // --- Hero layout (2026-09-13) --------------------------------------------
  // Deliberate asymmetry - the opposite instinct from the balance
  // preference above. Direct feedback comparing Live against this engine
  // side by side: "live looks nicer, testing looks a bit structured
  // rather than the predefined nice layouts." Live's retired template
  // system (findBestLayout/LAYOUTS) had named templates with an explicit
  // hero-photo-plus-small-accents hierarchy; this engine treats every
  // photo as equally important, and the balance preference above
  // actively pushes toward equal area per photo - moving further from
  // that curated feel, not closer, as it improved. Confirmed direction:
  // "Add hero bias to the tree" - reintroduce it as an explicit,
  // seed-chosen ALTERNATIVE to the normal (balanced) split, tried once
  // at the very top of a page's tree only, never at a deeper split -
  // one clear hero per page, not a recursively-fractal one.
  //
  // Which specific photo seeds the large leaf here doesn't matter:
  // buildTreeBoxesForPage's reassignLeavesByAspectMatch (photobook-
  // builder.liquid) re-derives every leaf's photo from box/photo aspect
  // afterwards regardless of the uuid a leaf was built with, so this
  // only needs to produce the SHAPE (one large box, several small ones)
  // - whichever real photo best fits that large box ends up there.
  // Starts at 4, not 3: measured that n=3 already produces a dominant
  // single-photo leaf on its own, with or without any of this - a lone
  // photo needs a close-to-square box to score well, which on a normal
  // page shape means giving it MORE area than the pair on the other
  // side, purely from the existing aspect-driven scoring (confirmed by
  // disabling hero mode entirely and re-measuring: still 100% of n=3
  // splits landing a leaf over 45% of the page, mixed or homogeneous
  // orientation alike). Explicit hero mode only adds something new
  // starting at n=4, where the existing balanced/oriented paths do NOT
  // naturally produce a dominant leaf (measured: 0% without this,
  // ~35% with it, matching HERO_PROBABILITY_PERCENT almost exactly).
  const HERO_MIN_PHOTOS = 4;
  const HERO_MAX_PHOTOS = 6;
  const HERO_PROBABILITY_PERCENT = 35;
  const HERO_RATIO_SAMPLES = [0.60, 0.65, 0.70, 0.75];

  // All (direction, ratio) combinations from HERO_RATIO_SAMPLES whose
  // hero-sized slot is itself a normal, strictly-compliant box (the
  // standard 2.0 ceiling, not the homogeneous-orientation exceptions
  // above - hero mode is independent of photo orientation entirely).
  function heroSplitCandidates(frame) {
    const out = [];
    LAYOUT_DIRECTIONS.forEach(dir => {
      HERO_RATIO_SAMPLES.forEach(r => {
        const [heroFrame] = divideFrame(frame, dir, r);
        if (aspectError(heroFrame) === 0) out.push({ direction: dir, ratio: r });
      });
    });
    return out;
  }

  // frame is always the true page frame here (hero only ever runs at
  // the top of buildTree, never nested) - all four sides are canvas
  // edges, so mirroring via 1-ratio (the same trick buildSubtreeInternal
  // uses for flip variety) is safe unconditionally, without needing
  // that function's symmetric-flags guard.
  function tryHeroLayout(photos, frame, variationSeed) {
    const candidates = heroSplitCandidates(frame);
    if (candidates.length === 0) return null;
    const pick = candidates[Math.abs(mixSeed(variationSeed, 11)) % candidates.length];
    const swapHero = (mixSeed(variationSeed, 13) & 1) === 1;
    const finalRatio = swapHero ? (1 - pick.ratio) : pick.ratio;
    const [slotFirst, slotSecond] = divideFrame(frame, pick.direction, finalRatio);
    const heroFrame = swapHero ? slotSecond : slotFirst;
    const restFrame = swapHero ? slotFirst : slotSecond;

    const heroIndex = Math.abs(mixSeed(variationSeed, 10)) % photos.length;
    const heroPhoto = photos[heroIndex];
    const rest = photos.slice(0, heroIndex).concat(photos.slice(heroIndex + 1));
    const heroLeaf = makeLeaf(heroPhoto.uuid);
    const restResult = buildSubtreeInternal(rest, restFrame, mixSeed(variationSeed, 12));

    const tree = swapHero
      ? makeSplit(pick.direction, finalRatio, restResult.tree, heroLeaf)
      : makeSplit(pick.direction, finalRatio, heroLeaf, restResult.tree);
    return { tree, ok: restResult.ok };
  }

  function buildTree(photos, dims, variationSeed) {
    const rootFrameObj = rootFrame(dims, photos.length === 1);
    if (variationSeed !== undefined && photos.length >= HERO_MIN_PHOTOS && photos.length <= HERO_MAX_PHOTOS) {
      const heroRoll = Math.abs(mixSeed(variationSeed, 9)) % 100;
      if (heroRoll < HERO_PROBABILITY_PERCENT) {
        const heroResult = tryHeroLayout(photos, rootFrameObj, variationSeed);
        if (heroResult && heroResult.ok) return heroResult.tree;
      }
    }
    return buildSubtree(photos, rootFrameObj, variationSeed);
  }

  // Internal worker - returns { tree, ok }, ok meaning "every leaf in
  // this subtree is either strictly compliant, or was a deliberate,
  // directly-checked relaxed exception at the exact split that produced
  // it." Propagated bottom-up (a level's own ok is true only if BOTH its
  // own immediate decision held AND both children's already were ok) so
  // a parent never has to re-walk or re-judge a child's leaves itself -
  // it only ever answers for its own choice, and trusts what recursion
  // already established. See buildSubtree below (the exported name) for
  // why this distinction from a flat "check every leaf against the
  // ceiling" exists at all: that flat version wrongly re-flagged a
  // child's OWN validly-relaxed leaf as a violation, since a leaf
  // relaxed several levels down isn't the immediate product of an
  // ancestor's split and so isn't in the ancestor's own relaxedUuids -
  // measured live, it was silently discarding the correct 3-landscape
  // result and falling back to the old shape every time.
  function buildSubtreeInternal(photos, frame, variationSeed) {
    if (photos.length === 1) return { tree: makeLeaf(photos[0].uuid), ok: true };

    const mid = Math.round(photos.length / 2);
    const groupA = photos.slice(0, mid);
    const groupB = photos.slice(mid);
    const singleSides = (groupA.length === 1 && groupB.length === 1) ? 'both'
      : (groupA.length === 1) ? 'A'
      : (groupB.length === 1) ? 'B'
      : null;

    function attempt(allowOrientation) {
      let { direction, ratio } = chooseSplit(frame, groupA, groupB, variationSeed, allowOrientation);
      ratio = clampNum(ratio, LAYOUT_MIN_RATIO, LAYOUT_MAX_RATIO);
      let immediateOk = true;
      if (singleSides) {
        // Tell ensureAspect about the same relaxed ceiling chooseSplit's
        // orientation-preference path may have already used to pick this
        // very (direction, ratio) - otherwise this always-strict pass
        // re-judges it against the plain 2.0 ceiling, finds it "wrong",
        // and quietly corrects it straight back, making that preference
        // path a no-op (measured, before this line existed).
        const relaxedMax = (allowOrientation && variationSeed !== undefined && isHomogeneousOrientation(photos))
          ? (singleSides === 'both' ? LAYOUT_ORIENTATION_BOTH_ASPECT_MAX : LAYOUT_ORIENTATION_ASPECT_MAX)
          : undefined;
        const adj = ensureAspect(frame, direction, ratio, singleSides, relaxedMax);
        direction = adj.direction; ratio = adj.ratio;
        const [checkA, checkB] = divideFrame(frame, direction, ratio);
        immediateOk = (singleSides === 'B' || aspectError(checkA, relaxedMax) === 0)
          && (singleSides === 'A' || aspectError(checkB, relaxedMax) === 0);
      }
      // Flip variety ("...or vert flip", direct request): swap which
      // physical slot - top/bottom for a horizontal split, left/right
      // for vertical - each group's content lands in, using 1-ratio so
      // each group keeps the exact size chooseSplit/ensureAspect
      // computed for it, only its position moves.
      //
      // Only safe when the frame's OWN boundary flags on the relevant
      // axis are symmetric (both true - typically the page edge itself
      // - or both already false/internal). When they're not (the usual
      // case one level or more into the tree: one side is a real canvas
      // edge with a full margin, the other an internal seam with only
      // a half-gap), 1-ratio does NOT mirror correctly - it swaps which
      // fraction of the frame each slot gets, but the top/bottom (or
      // left/right) flags stay tied to physical position, not to
      // fraction, so the group that "should" get the small margin/full
      // edge treatment ends up with the wrong one instead. Measured
      // live: exactly this produced a leaf at visible aspect 4.8 (both
      // ceilings badly blown) several levels deep, where the swapped
      // frame silently got the wrong inset. Restricting to symmetric
      // frames keeps every existing size/ensureAspect guarantee above
      // intact and still covers the actual request (the top-level split
      // - the whole page, all four sides true) and any other frame
      // whose relevant edges happen to already match.
      const flagsSymmetric = direction === 'horizontal' ? (frame.top === frame.bottom) : (frame.left === frame.right);
      const swap = flagsSymmetric && variationSeed !== undefined && (mixSeed(variationSeed, 5) & 1) === 1;
      const finalRatio = swap ? (1 - ratio) : ratio;
      const [frameFirst, frameSecond] = divideFrame(frame, direction, finalRatio);
      const seedA = variationSeed === undefined ? undefined : mixSeed(variationSeed, 1);
      const seedB = variationSeed === undefined ? undefined : mixSeed(variationSeed, 2);
      const childA = buildSubtreeInternal(groupA, swap ? frameSecond : frameFirst, seedA);
      const childB = buildSubtreeInternal(groupB, swap ? frameFirst : frameSecond, seedB);
      const tree = swap
        ? makeSplit(direction, finalRatio, childB.tree, childA.tree)
        : makeSplit(direction, finalRatio, childA.tree, childB.tree);
      return { tree, ok: immediateOk && childA.ok && childB.ok };
    }

    const useOrientation = variationSeed !== undefined && isHomogeneousOrientation(photos);
    if (!useOrientation) return attempt(false);

    // estimateWorstAspect (what chooseSplit's relaxed-orientation path
    // trusts for the side it can't directly check) is scale-invariant
    // and has no idea about the fixed-mm margin/gap inset - it can
    // promise a homogeneous multi-photo side will turn out fine when,
    // several levels deep, the frame it's actually left with is small
    // enough that the fixed inset eats a large share of it and no split
    // of it clears even the relaxed ceiling. Measured live with this
    // exact gap: n=9 portrait, 3000 seeds - 11/27000 leaves over the
    // relaxed ceiling with orientation preference off entirely (the
    // pre-existing, accepted baseline this feature must never make
    // meaningfully worse), 817/27000 by trusting the estimate alone.
    // ok, propagated bottom-up through real recursion rather than
    // another estimate, catches this: if the oriented attempt's own
    // choice, or anything it recursed into, couldn't actually achieve
    // its ceiling, ok is false and this falls back to attempt(false) -
    // the plain, pre-existing, already-proven-safe choice for this
    // split - never anything worse than before this feature existed.
    const oriented = attempt(true);
    return oriented.ok ? oriented : attempt(false);
  }

  function buildSubtree(photos, frame, variationSeed) {
    return buildSubtreeInternal(photos, frame, variationSeed).tree;
  }

  // --- Shrink-to-fit fallback -----------------------------------------------
  // If a canvas's own aspect ratio can't hold `photoCount` cells inside the
  // aspect band even with the lookahead above, don't stretch cells to fit -
  // shrink the USABLE area (what buildTree lays photos into) toward a
  // shape that can be filled well, and let the caller center it in the full
  // canvas, growing the margin on the long axis to absorb the difference.
  //
  // Floor: usable long-axis length can shrink to no less than 70% of the
  // canvas's own long-axis length (short axis is never touched). That caps
  // total margin growth at 30% of the long axis - 15% added to each side -
  // regardless of how extreme the input canvas ratio is, which is what
  // rules out "vast empty edges": the shrink is bounded by construction,
  // not by whether a within-band solution was found. If the floor is
  // reached without finding one, findUsableFrame returns the floor itself
  // (the best available correction) with `metBand: false`, rather than
  // continuing to shrink further in pursuit of a perfect fit.
  //
  // n=1 never reaches this floor at all - see the early return below - so
  // 0.7 only has to cover n>=2, where it's already sufficient on every
  // canvas ratio measured (see scripts/test-layout-engine.js). Not
  // lowered further: a tighter floor here would only widen the margin
  // ceiling for cases that don't currently need it.
  const USABLE_AREA_FLOOR_FRACTION = 0.7;

  function findUsableFrame(canvasMm, photoCount) {
    // A single photo has nothing to separate from - no margin, no gap, no
    // shrink, fills the canvas edge to edge. There's no cell aspect to
    // violate since there's no inset at all (rootFrame's fullBleed flag,
    // applied in buildTree/leafList), so the aspect band check is skipped
    // entirely here, not passed by chance - the loop below never runs.
    if (photoCount === 1) return { w: canvasMm.w, h: canvasMm.h, shrunk: false, metBand: true, fullBleed: true };

    const isWide = canvasMm.w >= canvasMm.h;
    const longAxis = isWide ? canvasMm.w : canvasMm.h;
    const shortAxis = isWide ? canvasMm.h : canvasMm.w;
    const floorLong = longAxis * USABLE_AREA_FLOOR_FRACTION;

    const steps = 20;
    for (let i = 0; i <= steps; i++) {
      const testLong = longAxis - (longAxis - floorLong) * (i / steps);
      const testW = isWide ? testLong : shortAxis;
      const testH = isWide ? shortAxis : testLong;
      if (estimateWorstAspect(photoCount, testW / testH) <= LAYOUT_ASPECT_MAX) {
        return { w: testW, h: testH, shrunk: i > 0, metBand: true };
      }
    }
    return {
      w: isWide ? floorLong : shortAxis,
      h: isWide ? shortAxis : floorLong,
      shrunk: true,
      metBand: false
    };
  }

  // Every leaf's resolved frame, via one tree walk. Needed by insertLeaf
  // (find the largest leaf) and by the eventual render/flatten step.
  // `dims` must be the same usable-area dims the tree was originally built
  // against (see findUsableFrame), not necessarily the raw canvas, or
  // resolved rects will be wrong. fullBleed is re-derived from the tree
  // shape itself (root is a bare leaf <=> exactly one photo) rather than
  // passed in, so it's automatically correct after any edit - insertLeaf
  // and removeLeaf never need to know about it or keep it in sync
  // themselves; a tree that goes from 1 photo to 2, or back down to 1,
  // transitions between full-bleed and normal margin/gap purely because
  // its root stops (or starts) being a bare leaf.
  function leafList(tree, dims) {
    const out = [];
    (function walk(node, frame) {
      if (node.type === 'leaf') { out.push({ uuid: node.uuid, frame }); return; }
      const [fa, fb] = divideFrame(frame, node.direction, node.ratio);
      walk(node.first, fa);
      walk(node.second, fb);
    })(tree, rootFrame(dims, tree.type === 'leaf'));
    return out;
  }

  // Same walk as leafList, but also tracks each leaf's top-left position
  // (in the same mm units as its frame), for rendering. Position isn't
  // needed by any of the tree-editing primitives above, only by the actual
  // DOM-application step (renderLayout, outside this module).
  function flattenForRender(tree, dims) {
    const out = [];
    (function walk(node, frame, x, y) {
      if (node.type === 'leaf') { out.push({ uuid: node.uuid, x, y, frame }); return; }
      const [fa, fb] = divideFrame(frame, node.direction, node.ratio);
      if (node.direction === 'horizontal') { walk(node.first, fa, x, y); walk(node.second, fb, x, y + fa.h); }
      else { walk(node.first, fa, x, y); walk(node.second, fb, x + fa.w, y); }
    })(tree, rootFrame(dims, tree.type === 'leaf'), 0, 0);
    return out;
  }

  // Decision #48 (docs/decisions-to-make.md), Phase 1: same walk shape as
  // flattenForRender, but collects every SPLIT node instead of every leaf
  // - one entry per node.ratio that's directly draggable to resize. `frame`
  // is the full frame THIS split divides (everything a consumer needs to
  // turn a drag gesture into a ratio delta: the split's own direction,
  // current ratio, and the pixel/mm extent its boundary line spans), `x`/
  // `y` is that frame's own top-left position, same coordinate space
  // flattenForRender's leaves use.
  //
  // Deliberately returns the REAL node objects from the tree (not copies)
  // - a consumer mutates `entry.node.ratio` directly and re-renders, no
  // replaceLeaf-style immutable rebuild needed, since a ratio tweak alone
  // never changes tree STRUCTURE (which leaf is where), only each split's
  // resolved frame size. Only valid for the exact tree snapshot this was
  // called against, same as any other node reference - re-call after any
  // structural edit (insertLeaf/removeLeaf/swapLeaves all return a NEW
  // tree), don't hold one across those.
  //
  // Only covers boundaries that ARE one split node - two leaves that look
  // adjacent on screen but sit in different branches of an ancestor split
  // don't share a single ratio at all. See splitBoundaryGroups below for
  // that case (decision #48 Phase 2).
  function splitBoundaries(tree, dims) {
    const out = [];
    (function walk(node, frame, x, y) {
      if (node.type === 'leaf') return;
      out.push({ node, frame, x, y });
      const [fa, fb] = divideFrame(frame, node.direction, node.ratio);
      if (node.direction === 'horizontal') { walk(node.first, fa, x, y); walk(node.second, fb, x, y + fa.h); }
      else { walk(node.first, fa, x, y); walk(node.second, fb, x + fa.w, y); }
    })(tree, rootFrame(dims, tree.type === 'leaf'), 0, 0);
    return out;
  }

  // Decision #48, Phase 2. A split boundary that looks like one continuous
  // line on screen (e.g. the horizontal line across a 2x2 grid built as
  // two independent vertical branches, each with its own horizontal split
  // underneath) is often actually several different split nodes that
  // merely happen to currently land on the same line - Phase 1's
  // splitBoundaries treats each as a separate handle, so dragging one
  // leaves the others behind and the "line" visibly kinks at the branch
  // boundary. This groups every splitBoundaries() entry with every OTHER
  // entry that shares its direction, lands on (within LINE_EPS_MM of) the
  // same line, and touches or overlaps its span along that line - then
  // transitively absorbs anything collinear with the growing group, so a
  // chain of 3+ aligned segments merges into one. A boundary with nothing
  // collinear next to it still comes back as its own group of 1, so this
  // is a strict superset of Phase 1's per-node behavior, not a
  // replacement for it.
  //
  // Each group: { direction, line, rangeStart, rangeEnd, entries }, where
  // `line` is the shared cut position (x for vertical, y for horizontal)
  // in the SAME mm units as `dims`, `rangeStart`/`rangeEnd` is the union
  // span along the perpendicular axis (for sizing one handle to cover the
  // whole visual line), and `entries` is the underlying splitBoundaries()
  // rows that make up the group - each still carries its own real `node`
  // (mutate `entries[i].node.ratio` directly, same contract as Phase 1)
  // and `frame` (each entry's own parent frame, needed to convert a
  // shared mm delta into THIS entry's own ratio delta - see
  // canvas-builder.liquid's wireResizeDrag for why the same mm delta
  // applied via each entry's own frame size keeps the line straight
  // during a drag even when the entries' frames differ in size).
  const LINE_EPS_MM = 0.5;
  function splitBoundaryGroups(tree, dims) {
    const all = splitBoundaries(tree, dims);
    const used = new Array(all.length).fill(false);
    const groups = [];
    for (let i = 0; i < all.length; i++) {
      if (used[i]) continue;
      const first = all[i];
      const isVertical = first.node.direction === 'vertical';
      const line = isVertical ? (first.x + first.frame.w * first.node.ratio) : (first.y + first.frame.h * first.node.ratio);
      let rangeStart = isVertical ? first.y : first.x;
      let rangeEnd = isVertical ? first.y + first.frame.h : first.x + first.frame.w;
      const entries = [first];
      used[i] = true;
      let grew = true;
      while (grew) {
        grew = false;
        for (let j = 0; j < all.length; j++) {
          if (used[j]) continue;
          const cand = all[j];
          if (cand.node.direction !== first.node.direction) continue;
          const candLine = isVertical ? (cand.x + cand.frame.w * cand.node.ratio) : (cand.y + cand.frame.h * cand.node.ratio);
          if (Math.abs(candLine - line) > LINE_EPS_MM) continue;
          const candStart = isVertical ? cand.y : cand.x;
          const candEnd = isVertical ? cand.y + cand.frame.h : cand.x + cand.frame.w;
          if (candEnd < rangeStart - LINE_EPS_MM || candStart > rangeEnd + LINE_EPS_MM) continue; // doesn't touch the group's span yet
          entries.push(cand);
          used[j] = true;
          rangeStart = Math.min(rangeStart, candStart);
          rangeEnd = Math.max(rangeEnd, candEnd);
          grew = true;
        }
      }
      groups.push({ direction: first.node.direction, line, rangeStart, rangeEnd, entries });
    }
    return groups;
  }

  // Swaps which photo occupies which leaf. No structural edit at all -
  // ratios, directions, regions are all untouched, only the two uuids
  // trade places. This is the real swap the design calls for, replacing
  // "drag reorders DOM, dense-flow interprets the new order."
  function swapLeaves(tree, uuidA, uuidB) {
    if (tree.type === 'leaf') {
      if (tree.uuid === uuidA) return makeLeaf(uuidB);
      if (tree.uuid === uuidB) return makeLeaf(uuidA);
      return tree;
    }
    const newFirst = swapLeaves(tree.first, uuidA, uuidB);
    const newSecond = swapLeaves(tree.second, uuidA, uuidB);
    if (newFirst === tree.first && newSecond === tree.second) return tree;
    return makeSplit(tree.direction, tree.ratio, newFirst, newSecond);
  }

  function collectUuids(tree) {
    if (tree.type === 'leaf') return [tree.uuid];
    return collectUuids(tree.first).concat(collectUuids(tree.second));
  }

  // Removes uuid's leaf. Returns the new root (or null if it was the only
  // leaf). Collapses the removed leaf's parent split so the sibling
  // subtree absorbs the freed area exactly - that union is precisely what
  // the removed split node represented, so no other leaf is touched and no
  // leftover space is possible. Pure recursion, no parent pointers needed.
  function removeLeaf(tree, uuid) {
    if (tree.type === 'leaf') return (tree.uuid === uuid) ? null : tree;
    if (tree.first.type === 'leaf' && tree.first.uuid === uuid) return tree.second;
    if (tree.second.type === 'leaf' && tree.second.uuid === uuid) return tree.first;
    const newFirst = removeLeaf(tree.first, uuid);
    if (newFirst !== tree.first) return makeSplit(tree.direction, tree.ratio, newFirst, tree.second);
    const newSecond = removeLeaf(tree.second, uuid);
    if (newSecond !== tree.second) return makeSplit(tree.direction, tree.ratio, tree.first, newSecond);
    return tree; // uuid not found in this subtree
  }

  function replaceLeaf(tree, uuid, replacement) {
    if (tree.type === 'leaf') return (tree.uuid === uuid) ? replacement : tree;
    const newFirst = replaceLeaf(tree.first, uuid, replacement);
    if (newFirst !== tree.first) return makeSplit(tree.direction, tree.ratio, newFirst, tree.second);
    const newSecond = replaceLeaf(tree.second, uuid, replacement);
    if (newSecond !== tree.second) return makeSplit(tree.direction, tree.ratio, tree.first, newSecond);
    return tree;
  }

  // Inserts a new photo. sizeHint: 1 (normal) / 2 (bigger) / 4 (biggest) -
  // matches the 4-tier resize cycle's relative "how much bigger" request.
  // Splits the CURRENT LARGEST leaf (never shrinks an already-small cell
  // further) and gives the new photo a bigger ratio share for a bigger
  // hint, clamped to the same aspect-safe range buildTree uses. Used both
  // for "add a new photo" (sizeHint 1) and, via remove-then-insert, for
  // per-tile resize (see design doc).
  function insertLeaf(tree, photo, sizeHint, dims) {
    if (!tree) return makeLeaf(photo.uuid); // empty collage - first photo

    const leaves = leafList(tree, dims);
    let target = leaves[0];
    leaves.forEach(entry => { if (entry.frame.w * entry.frame.h > target.frame.w * target.frame.h) target = entry; });

    const r = photo.width / photo.height;
    let direction = (r > 1.05) ? 'horizontal' : (r < 0.95) ? 'vertical' : (target.frame.w >= target.frame.h ? 'vertical' : 'horizontal');
    const newPhotoShare = sizeHint >= 4 ? 0.7 : sizeHint >= 2 ? 0.6 : 0.5;
    let ratio = clampNum(1 - newPhotoShare, LAYOUT_MIN_RATIO, LAYOUT_MAX_RATIO); // side A (first) keeps the EXISTING photo

    const adj = ensureAspect(target.frame, direction, ratio, 'both');
    direction = adj.direction; ratio = adj.ratio;

    const newSplit = makeSplit(direction, ratio, makeLeaf(target.uuid), makeLeaf(photo.uuid));
    return replaceLeaf(tree, target.uuid, newSplit);
  }

  // Draft migration: old drafts have no tree at all (flat per-photo
  // `span`). Never attempts to reconstruct a tree from that - there's no
  // canonical answer, many trees can produce the same flat arrangement.
  // If a saved tree exists and its leaf set exactly matches the restored
  // photos, use it; otherwise (missing entirely, or stale because a photo
  // failed to restore) build a fresh one from the current photo list.
  function treeMatchesPhotos(tree, photos) {
    if (!tree) return false;
    const treeUuids = collectUuids(tree).sort();
    const photoUuids = photos.map(p => p.uuid).sort();
    if (treeUuids.length !== photoUuids.length) return false;
    return treeUuids.every((u, i) => u === photoUuids[i]);
  }

  function resolveTreeForRestore(savedTree, photos, canvasMm) {
    if (treeMatchesPhotos(savedTree, photos)) return savedTree;
    const usable = findUsableFrame(canvasMm, photos.length);
    return buildTree(photos, usable);
  }

  const GuillotineLayout = {
    makeLeaf, makeSplit, clampNum, rootFrame, divideFrame, visibleDims, visibleAspect, aspectError,
    voteDirection, ensureAspect, estimateWorstAspect, chooseSplit, buildTree, buildSubtree,
    findUsableFrame, leafList, flattenForRender, splitBoundaries, splitBoundaryGroups, swapLeaves, collectUuids, removeLeaf, replaceLeaf,
    insertLeaf, treeMatchesPhotos, resolveTreeForRestore,
    LAYOUT_MIN_RATIO, LAYOUT_MAX_RATIO, LAYOUT_ASPECT_MIN, LAYOUT_ASPECT_MAX, LAYOUT_GAP_MM,
    LAYOUT_MARGIN_MM, USABLE_AREA_FLOOR_FRACTION
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = GuillotineLayout;
  } else {
    root.GuillotineLayout = GuillotineLayout;
  }
})(typeof window !== 'undefined' ? window : this);
